import { useEffect } from "react";
import { brandLogo } from "../../asset";
import FileUpload from "../../components/common/FileUpload";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import ToggleSwitch from "../../components/ToggleSwitch";
import SubHeadings from "../../components/SubHeadings";
import { OtherDetailsType } from "../../types/schoolBranchType";
import { useFormEdit } from "../../util/hooks/useFormEdit";
import SchoolBranchApis from "../../api/schoolBranchApis";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { toast } from "sonner";

export default function OtherDetails() {
  const { formData, setFormData, isEditing } = useFormEdit<OtherDetailsType>(
    {} as OtherDetailsType
  );

  const branchId = useSelector(
    (state: RootState) => state.access.schoolBranch?.schoolBranchId
  );

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "radio"
          ? value === "true"
          : name.includes("RunningNumber")
          ? Number(value)
          : value,
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await SchoolBranchApis.getOtherDetails(branchId);
        setFormData(res.data);
      } catch (error) {
        console.error("Failed to load details:", error);
      }
    };

    fetchData();
  }, [setFormData, branchId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await SchoolBranchApis.updateOtherDetails(branchId, formData);
      toast.success("Changes saved successfully.");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Something went wrong while saving changes. Please try again.";

      toast.error(message);
    }
  };

  return (
    <form className="px-4 md:px-9 grid gap-20" onSubmit={handleSubmit}>
      <article className="flex flex-col gap-7 w-full py-7">
        <SubHeadings text="Admission Number Setup" />
        <div className="grid md:grid-cols-2 gap-7">
          <CustomInput
            name="admissionNumberFormat"
            label="Admission number format"
            readOnly={true}
            type="text"
            placeholder="To be auto generated"
            value={formData?.admissionNumberFormat}
            onChange={handleInputChange}
            inputClassName="w-full"
          />
          <CustomInput
            name="admissionRunningNumber"
            label="Admission running number"
            readOnly={!isEditing}
            type="text"
            placeholder="No value"
            value={formData?.admissionRunningNumber?.toString() ?? ""}
            onChange={handleInputChange}
            inputClassName="w-full"
          />
        </div>
      </article>

      <article className="flex flex-col gap-7 w-full">
        <SubHeadings text="Employee Code Setup" />
        <div
          className="grid md:grid-cols-2 gap-7"
          aria-label="Employee code setup form">
          <CustomInput
            name="employeeCodeFormat"
            label="Employee code format"
            readOnly={!isEditing}
            type="text"
            placeholder="No value"
            value={formData?.employeeCodeFormat}
            onChange={handleInputChange}
            inputClassName="w-full"
          />
          <CustomInput
            name="employeeCodeRunningNumber"
            label="Employee code running number"
            readOnly={!isEditing}
            type="text"
            placeholder="No value"
            value={formData?.employeeCodeRunningNumber?.toString() ?? ""}
            onChange={handleInputChange}
            inputClassName="w-full"
          />
        </div>
      </article>

      <article className="flex flex-col gap-7 w-full">
        <SubHeadings text="Finance Details" />
        <div className="grid gap-7" aria-label="Finance details form">
          <CustomInput
            name="trustCompanyName"
            label="Trust/Company name"
            readOnly={!isEditing}
            type="text"
            placeholder="No value"
            value={formData?.trustCompanyName}
            onChange={handleInputChange}
            inputClassName="w-full"
          />
          <CustomInput
            name="registeredAddress"
            label="Registered address"
            readOnly={!isEditing}
            type="text"
            placeholder="No value"
            value={formData?.registeredAddress}
            onChange={handleInputChange}
            inputClassName="w-full"
          />

          <div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-7"
            aria-label="Tax and registration information">
            <CustomInput
              name="gstNumber"
              label="GST number"
              readOnly={!isEditing}
              type="text"
              placeholder="No value"
              value={formData?.gstNumber}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="panNumber"
              label="PAN number"
              readOnly={!isEditing}
              type="text"
              placeholder="No value"
              value={formData?.panNumber}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="cinNumber"
              label="CIN number"
              readOnly={!isEditing}
              type="text"
              placeholder="No value"
              value={formData.cinNumber}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="registrationNumber"
              label="Registration number"
              readOnly={!isEditing}
              type="text"
              placeholder="No value"
              value={formData.registrationNumber}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="udyamNumber"
              label="UDYAM Number"
              readOnly={!isEditing}
              type="text"
              placeholder="No value"
              value={formData.udyamNumber}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="invoiceReceiptFooter"
              label="Invoice receipt footer"
              readOnly={!isEditing}
              type="text"
              placeholder="No value"
              value={formData.invoiceReceiptFooter}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
          </div>

          <fieldset className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-7">
              <label className="text-sm font-semibold text-neutral-400">
                Do you want newly created invoices to include GST?
              </label>
              <div className="flex items-center gap-4" role="radiogroup">
                <label className="flex items-center gap-2 cursor-pointer">
                  <CustomInput
                    name="isGSTEnabled"
                    type="radio"
                    checked={formData?.isGSTEnabled === true}
                    value="true"
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    inputClassName="w-3 h-3"
                    aria-label="Include GST"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      formData.isGSTEnabled === true
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}>
                    Yes
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <CustomInput
                    name="isGSTEnabled"
                    type="radio"
                    checked={formData.isGSTEnabled === false}
                    value="false"
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    inputClassName="w-3 h-3"
                    aria-label="Exclude GST"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      formData.isGSTEnabled === false
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}>
                    No
                  </span>
                </label>
              </div>
            </div>

            <div className="flex items-center gap-7">
              <label className="text-sm font-semibold text-neutral-400">
                Reset invoice number every academic year?
              </label>
              <ToggleSwitch
                disabled={!isEditing}
                aria-label="Toggle yearly invoice reset"
                defaultChecked={formData?.resetInvoiceNumberEveryAcademicYear}
                onToggle={() =>
                  setFormData((p) => ({
                    ...p,
                    resetInvoiceNumberEveryAcademicYear:
                      !p.resetInvoiceNumberEveryAcademicYear,
                  }))
                }
              />
            </div>
          </fieldset>
        </div>

        <div className="flex justify-start mt-4">
          <FileUpload
            onUploadComplete={() => {}}
            className="md:w-72 rounded-md w-36 h-40"
            value={formData?.letterheadLogoUrl}
            disabled={!isEditing}
            label="Letter head logo"
            buttonText="Choose file"
            placeholderImage={brandLogo}
            buttonColor="bg-primary-800"
          />
        </div>
      </article>

      <div className="flex justify-end items-end mt-4">
        <CustomButton
          text="Save Changes"
          color="bg-primary-500"
          disabled={!isEditing}
          type="submit"
        />
      </div>
    </form>
  );
}
